import { router } from '@/router/router';
import { useActivityProjectStore } from '@/store/activityProjectStore';
import { useActivityTemplateStore } from '@/store/activityTemplateStore';
import { useProjectStore } from '@/store/projectStore/projectStore';
import { useTemplateStore } from '@/store/templateStore/templateStore';
import {
  addToPlan,
  emptyPlan,
  isEmptyPlan,
  mergePlan,
  type AiLiveMode,
  type RefreshPlan,
} from './liveTargets';
import type { AiStep } from '@beeboard/common';

/**
 * Brings each entity an AI answer saves onto the page while the answer runs
 * (WI #5767, D4): the map, the list and the kanban board all read the
 * project / template stores, so refreshing an entity there shows it at once.
 * The run's own socket events are dropped as the user's own, and templates
 * have none, so the steps of the chat stream drive it.
 *
 * Steps that arrive close together are applied together (`SYNC_DELAY_MS`),
 * one batch at a time, so an older response never overwrites a newer one. A
 * step without an endpoint of its own on the page (a phase, a workstream, a
 * milestone, a function) reloads the whole project / template, at most once
 * per `FULL_RELOAD_INTERVAL_MS` — not once per step. A batch that fails is
 * kept and tried again, so no single entity gets ahead of a reload it needs.
 * The reload when the answer ends stays the authoritative one.
 */

export const SYNC_DELAY_MS = 250;
export const FULL_RELOAD_INTERVAL_MS = 1000;
/** The longest wait before a failed batch is tried again. */
export const MAX_RETRY_MS = 8000;
/** Parallel activity requests of one batch. */
const FETCH_CONCURRENCY = 6;

async function eachLimited(
  ids: Iterable<string>,
  work: (id: string) => Promise<unknown>,
  alive: () => boolean,
): Promise<void> {
  const all = [...ids];
  for (let i = 0; i < all.length && alive(); i += FETCH_CONCURRENCY) {
    await Promise.all(
      all.slice(i, i + FETCH_CONCURRENCY).map((id) =>
        // An entity that no longer loads is left to the reload at the end.
        work(id).catch(() => undefined),
      ),
    );
  }
}

/**
 * Whether the page shows this project / template. One the user left is
 * loaded again when they come back (ProjectRoot, TemplateRoot).
 */
function isOnScreen(mode: AiLiveMode, contextId: string): boolean {
  const { params } = router.currentRoute.value;

  return (
    (mode === 'template' ? params.templateId : params.projectId) === contextId
  );
}

async function refreshProject(
  projectId: string,
  plan: RefreshPlan,
  alive: () => boolean,
): Promise<void> {
  const projectStore = useProjectStore();
  // Never loaded in this tab, or not on screen: the reload when the user
  // comes back, or at the end of the answer, covers it.
  if (
    !projectStore.hasProject(projectId) ||
    !isOnScreen('project', projectId)
  ) {
    return;
  }
  const activityStore = useActivityProjectStore();
  if (plan.full) {
    // Cached details first, so no render sees the fresh project next to a
    // stale sidebar (as at the end of a run).
    await eachLimited(
      [...plan.activities].filter((id) =>
        activityStore.hasActivity(projectId, id),
      ),
      (id) => activityStore.loadActivity(projectId, id),
      alive,
    );
    if (alive()) {
      await projectStore.loadProject(projectId);
    }
  } else {
    await eachLimited(
      plan.activities,
      (id) => activityStore.refreshActivity(projectId, id),
      alive,
    );
  }
  if (plan.members) {
    await projectStore.loadMembers(projectId);
  }
}

async function refreshTemplate(
  templateId: string,
  plan: RefreshPlan,
  alive: () => boolean,
): Promise<void> {
  const templateStore = useTemplateStore();
  if (
    !templateStore.hasTemplate(templateId) ||
    !isOnScreen('template', templateId)
  ) {
    return;
  }
  const activityStore = useActivityTemplateStore();
  if (plan.full) {
    await eachLimited(
      [...plan.activities].filter((id) =>
        activityStore.hasActivity(templateId, id),
      ),
      (id) => activityStore.loadActivity(templateId, id),
      alive,
    );
    // The template carries its document and link catalogs.
    if (alive()) {
      await templateStore.loadTemplate(templateId);
    }

    return;
  }
  // Catalogs first: an activity's new document or link resolves by id.
  await Promise.all([
    plan.documents
      ? templateStore.fetchDocuments(templateId).catch(() => undefined)
      : undefined,
    plan.links
      ? templateStore.fetchLinks(templateId).catch(() => undefined)
      : undefined,
  ]);
  await eachLimited(
    plan.activities,
    (id) => activityStore.refreshActivity(templateId, id),
    alive,
  );
}

/** The live refresh of one running answer's project or template. */
export class LiveSync {
  private plan = emptyPlan();
  private steps: AiStep[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  /** The batch being applied right now. */
  private flushing: Promise<void> | null = null;
  private lastFullAt = Number.NEGATIVE_INFINITY;
  /** Batches failed in a row. */
  private failures = 0;
  private disposed = false;

  constructor(
    private readonly mode: AiLiveMode,
    private readonly contextId: string,
    /** The steps of a batch, once the page shows them. */
    private readonly onApplied: (steps: AiStep[]) => void,
  ) {}

  add(step: AiStep): void {
    if (this.disposed) {
      return;
    }
    addToPlan(this.plan, this.mode, step);
    this.steps.push(step);
    this.schedule();
  }

  /**
   * The answer ended: its reload takes over, nothing pending runs, and a
   * batch being applied sends no further request.
   */
  dispose(): void {
    this.disposed = true;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  /** Resolves once no batch is being applied — before the final reload. */
  idle(): Promise<void> {
    return this.flushing ?? Promise.resolve();
  }

  private schedule(): void {
    if (this.timer || this.flushing || this.disposed) {
      return;
    }
    let delay = this.plan.full
      ? Math.max(
          SYNC_DELAY_MS,
          FULL_RELOAD_INTERVAL_MS - (Date.now() - this.lastFullAt),
        )
      : SYNC_DELAY_MS;
    if (this.failures) {
      delay = Math.max(
        delay,
        Math.min(MAX_RETRY_MS, 1000 * 2 ** (this.failures - 1)),
      );
    }
    this.timer = setTimeout(() => {
      this.timer = null;
      this.flushing = this.flush().finally(() => {
        this.flushing = null;
        if (this.steps.length) {
          this.schedule();
        }
      });
    }, delay);
  }

  private async flush(): Promise<void> {
    const plan = this.plan;
    const steps = this.steps;
    this.plan = emptyPlan();
    this.steps = [];
    const alive = () => !this.disposed;
    try {
      if (!isEmptyPlan(plan)) {
        if (plan.full) {
          this.lastFullAt = Date.now();
        }
        if (this.mode === 'template') {
          await refreshTemplate(this.contextId, plan, alive);
        } else {
          await refreshProject(this.contextId, plan, alive);
        }
      }
    } catch (err) {
      console.warn('Failed to show the AI assistant step on the page:', err);
      // Again later, with what came in meanwhile: a later batch must not
      // show an Eckpunkt in a phase this one failed to load.
      mergePlan(plan, this.plan);
      this.plan = plan;
      this.steps = [...steps, ...this.steps];
      this.failures++;

      return;
    }
    this.failures = 0;
    if (!this.disposed) {
      this.onApplied(steps);
    }
  }
}
